import React, { useState } from "react";
import InViewReveal from "./InViewReveal";
import SectionWatermark from "./SectionWatermark";
import RegistryRequestModal from "./RegistryRequestModal";

/**
 * Provenance - research-driven trust-proof section.
 *
 * Satisfies Stanford Web Credibility Guidelines:
 *   #2 Real organization (registered office, IEC, GST)
 *   #3 Expertise (years, divisions, volume)
 *   #4 Real people (founders with titles + bios)
 *   #5 Easy contact (multiple channels)
 *   #1 Verify info accuracy (outbound links to gov registries)
 *
 * Empty TODO slots are clearly marked - replace with real values; do NOT
 * publish placeholder values (faking a license number violates guideline #1).
 */

const REGISTRY = [
  // Each entry will render as a ledger row. The `verifyUrl` opens the
  // government registry's public lookup so visitors can verify themselves -
  // this is what NN/g calls "outbound trust" and is one of the strongest
  // credibility signals available.
  {
    label: "IEC Code",
    value: "TODO_IEC_NUMBER", // 10-digit Importer-Exporter Code
    verifyLabel: "Verify on DGFT",
    verifyUrl: "https://www.dgft.gov.in/CP/?opt=view-iec",
    requestType: "IEC",
    requestLabel: "Request IEC",
    requestModalLabel: "IEC details",
  },
  {
    label: "FSSAI License",
    value: "TODO_FSSAI_NUMBER", // 14-digit FSSAI license
    verifyLabel: "Verify on FSSAI",
    verifyUrl: "https://foscos.fssai.gov.in/",
    requestType: "FSSAI",
    requestLabel: "Request FSSAI",
    requestModalLabel: "FSSAI license",
  },
  {
    label: "APEDA Reg.",
    value: "TODO_APEDA_RCMC", // RCMC number
    verifyLabel: "Verify on APEDA",
    verifyUrl: "https://apeda.gov.in/apedawebsite/",
    requestType: "APEDA",
    requestLabel: "Request APEDA",
    requestModalLabel: "APEDA registration",
  },
  {
    label: "GSTIN",
    verifyLabel: "Verify on GST portal",
    verifyUrl: "https://services.gst.gov.in/services/searchtp",
    requestType: "GST",
    requestLabel: "Request GST",
    requestModalLabel: "GSTIN details",
  },
];

const FOUNDERS = [
  {
    name: "D. Anadkat",
    role: "Founder",
    bio: "Built Vilora Impex with a trader's discipline and a founder's patience. His work is simple in principle and strict in practice: quality must be visible, paperwork must be clean, and every shipment must protect the buyer's trust.",
  },
  {
    name: "H. Dudhatra",
    role: "Co-founder",
    bio: "Leads procurement with a sharp eye for origin, grading, and supplier reliability. He believes good export begins before the container is booked, with honest sourcing, careful selection, and no compromise on what leaves the warehouse.",
  },
];

export default function Provenance() {
  const [registryRequest, setRegistryRequest] = useState(null);

  return (
    <section
      id="provenance"
      className="provenance-section relative paper"
      style={{
        borderTop: "1px solid var(--rule)",
        borderBottom: "1px solid var(--rule)",
        overflow: "hidden",
      }}
    >
      <SectionWatermark variant="compass" position="top-left" size={420} opacity={0.18} />
      <div className="relative px-6 sm:px-10 py-14 md:py-20" style={{ zIndex: 1 }}>
        <InViewReveal className="flex items-baseline gap-4 mb-10 md:mb-14">
          <span className="folio">№ 05</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>
            PROVENANCE
          </span>
        </InViewReveal>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Left - the headline + standfirst */}
          <InViewReveal delay={2} className="md:col-span-5">
            <h2
              className="display"
              style={{
                fontSize: "clamp(36px, 4.6vw, 64px)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
              }}
            >
              Verified
              <br />
              <em
                className="display-italic"
                style={{ color: "var(--vermilion)" }}
              >
                where it counts.
              </em>
            </h2>
            <p
              className="mt-6"
              style={{
                fontSize: "clamp(16px, 1.2vw, 19px)",
                lineHeight: 1.65,
                color: "var(--ink-soft)",
                maxWidth: "44ch",
              }}
            >
              Every claim on this page is checkable on a government registry.
              We list our license numbers below; tap{" "}
              <em>verify</em> and the public lookup opens in a new tab. We&rsquo;d
              rather you trust the system than us.
            </p>

            <div className="rule-engraved my-8" />

            {/* Founders - Stanford #4 */}
            <div className="mb-3">
              <div className="folio mb-4" style={{ color: "var(--brass-deep)" }}>
                THE PEOPLE
              </div>
              <div className="grid gap-4">
                {FOUNDERS.map((p) => (
                  <div key={p.name} className="provenance-person">
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span
                        className="display-italic"
                        style={{ fontSize: 22, color: "var(--ink)" }}
                      >
                        {p.name}
                      </span>
                      <span className="meta" style={{ color: "var(--brass-deep)" }}>
                        {p.role}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.55,
                        color: "var(--ink-mute)",
                      }}
                    >
                      {p.bio}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </InViewReveal>

          {/* Right - registry card + office card */}
          <InViewReveal delay={3} className="md:col-span-7 lg:col-span-6 lg:col-start-7">
            <div className="provenance-card paper">
              <div className="provenance-card-head">
                <div>
                  <div className="folio">REGISTRY · A</div>
                  <div className="meta mt-1">PUBLIC RECORDS - VERIFIABLE</div>
                </div>
                <div
                  className="meta hidden sm:block"
                  style={{ color: "var(--brass-deep)" }}
                >
                  ✓ INDIA · 2025
                </div>
              </div>

              <div className="provenance-table">
                {REGISTRY.map((r) => (
                  <div key={r.label} className="provenance-row">
                    <div className="provenance-row-label">
                      <span className="folio" style={{ color: "var(--ink-mute)" }}>
                        {r.label}
                      </span>
                    </div>
                    <div className="provenance-row-value">
                      <span
                        className="mono"
                        style={{
                          fontSize: 14.5,
                          letterSpacing: "0.04em",
                          color: "var(--ink)",
                        }}
                      >
                        {r.value?.startsWith("TODO_") ? null : r.value}
                      </span>
                      <div className="provenance-registry-actions">
                        {r.requestType ? (
                          <button
                            type="button"
                            className="provenance-request"
                            onClick={() =>
                              setRegistryRequest({
                                type: r.requestType,
                                label: r.requestModalLabel,
                              })
                            }
                          >
                            {r.requestLabel}
                          </button>
                        ) : null}
                        <a
                          href={r.verifyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="provenance-verify"
                          aria-label={`${r.verifyLabel} (opens in a new tab)`}
                        >
                          {r.verifyLabel}
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                              d="M2 8l6-6M3.5 2H8v4.5"
                              stroke="currentColor"
                              strokeWidth="1.2"
                              strokeLinecap="square"
                            />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Office - Stanford #2 (real organization). Empty slot kept honest. */}
              <div className="provenance-office">
                <div
                  className="folio mb-3"
                  style={{ color: "var(--brass-deep)" }}
                >
                  REGISTERED OFFICE
                </div>
                <div
                  style={{
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: "var(--ink)",
                    fontStyle: "italic",
                    fontFamily: "'Cormorant Garamond', serif",
                  }}
                >
                  {/* TODO: replace with full registered address.
                      Until you provide it, only the city + state shows. */}
                  Vilora Organic Essential Pvt. Ltd.
                  <br />
                  <span style={{ color: "var(--ink-mute)" }}>
                    Rajkot, Gujarat - India
                  </span>
                </div>
                <div className="provenance-office-actions">
                  <a
                    href="https://www.google.com/maps/search/Rajkot+Gujarat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="provenance-link"
                  >
                    View on Map
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 8l6-6M3.5 2H8v4.5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="square"
                      />
                    </svg>
                  </a>
                  <span className="provenance-divider" aria-hidden="true">·</span>
                  <a href="mailto:exports@viloraimpex.com" className="provenance-link">
                    exports@viloraimpex.com
                  </a>
                  <span className="provenance-divider" aria-hidden="true">·</span>
                  <a href="tel:+918669999901" className="provenance-link">
                    +91 86699 99901
                  </a>
                </div>
              </div>
            </div>
          </InViewReveal>
        </div>
      </div>
      <RegistryRequestModal
        open={!!registryRequest}
        documentType={registryRequest?.type}
        documentLabel={registryRequest?.label}
        onClose={() => setRegistryRequest(null)}
      />
    </section>
  );
}
